import { http } from '@noor/api';
import { env } from '@noor/config';
import {
  sessionUserSchema,
  loginResponseSchema,
  type SessionUser,
  type LoginCredentials,
  type ChangePasswordPayload,
} from '../model/schema';
import { decodeAccessTokenPayload, normalizeRole } from '../lib/auth-payload';
import { getDeviceId, getDeviceName, WEB_PLATFORM } from '../lib/device-identity';

/** Unwrap a `{ data: … }` envelope if present (the backend nests payloads). */
function unwrap(raw: unknown): unknown {
  return raw && typeof raw === 'object' && 'data' in raw ? raw.data : raw;
}

export const sessionApi = {
  /** Current-user endpoint. */
  async me(): Promise<SessionUser> {
    const raw = await http.get<unknown>('admin/auth/me');
    return sessionUserSchema.parse(unwrap(raw));
  },

  /**
   * Admin login on the auth service (cpanel `logInCore`):
   * `POST {authHost}/api/v1/auth/user` — never the main gateway.
   *
   * cpanel parity: the device identity is always attached (it names the row
   * on the sessions screen). The auth service answers
   * `{ data: { access_token, refresh_token } }` with NO user object, so the
   * role/phone/id come from the JWT payload, exactly like cpanel's
   * `LoginPage.onSuccess`.
   */
  async login(credentials: LoginCredentials): Promise<{
    token: string;
    refresh_token?: string | undefined;
    user?: SessionUser | undefined;
  }> {
    const body: LoginCredentials = {
      ...credentials,
      device_id: credentials.device_id ?? getDeviceId(),
      device_name: credentials.device_name ?? getDeviceName(),
      platform: credentials.platform ?? WEB_PLATFORM,
    };
    const raw = await http.post<unknown>('/api/v1/auth/user', body, {
      baseURL: env.VITE_AUTH_API_URL,
      skipAuthRetry: true,
    });
    const parsed = loginResponseSchema.parse(unwrap(raw));
    // The schema refine guarantees one of the two token keys.
    const token = parsed.token ?? parsed.access_token ?? '';

    if (parsed.user)
      return { refresh_token: parsed.refresh_token, user: parsed.user, token };

    // Token-only response: recover identity from the JWT like cpanel did.
    const payload = decodeAccessTokenPayload(token);
    const role = normalizeRole(payload?.role) ?? '';
    const phone = payload?.phone ?? credentials.phone;
    const sub = payload?.sub ? Number(payload.sub) : NaN;
    return {
      refresh_token: parsed.refresh_token,
      token,
      ...(role || phone || Number.isFinite(sub)
        ? {
            user: {
              user_id: Number.isFinite(sub) ? sub : 0,
              first_name: null,
              last_name: null,
              role,
            },
          }
        : {}),
    };
  },

  /** Log out — `POST /auth/log-out`. Best-effort; always clear the local token. */
  async logout(): Promise<void> {
    // ignore failures — the local token is cleared regardless
    await http.post<unknown>('auth/log-out', { firebase_token: null }).catch(Boolean);
  },

  /** Register this browser's Firebase Cloud Messaging token for chat pushes. */
  async saveFirebaseToken(token: string): Promise<void> {
    await http.post<unknown>('auth/save-token', { token });
  },

  /**
   * Change the current user's password on the auth service:
   * `POST {authHost}/api/v1/auth/change-password`
   * (`{old_password, new_password}` — never the main gateway).
   * Answers `{ data: {}, meta: … }`, so there is nothing to parse.
   */
  async changePassword(payload: ChangePasswordPayload): Promise<void> {
    await http.post<unknown>('/api/v1/auth/change-password', payload, {
      baseURL: env.VITE_AUTH_API_URL,
    });
  },

  async sendPasswordResetOtp(phone: string): Promise<{ finish_time?: string }> {
    const raw = await http.post<unknown>(
      '/api/v1/otp/send',
      { phone, app: 'user' },
      {
        baseURL: env.VITE_AUTH_API_URL,
        skipAuthRetry: true,
      },
    );
    const data = unwrap(raw);
    return data && typeof data === 'object' ? data : {};
  },

  async resetUserPassword(payload: { phone: string; otp: string }): Promise<void> {
    await http.post<unknown>('/api/v1/auth/users/reset-password', payload, {
      baseURL: env.VITE_AUTH_API_URL,
      skipAuthRetry: true,
      headers: { 'X-Platform': 'web' },
    });
  },
};
